import { useEffect, useRef } from "react"

import { CaptionRevealScheduler } from "@/lib/voice-chat/caption-pacing"
import { voiceTracer } from "@/lib/voice-trace/voice-trace"

const captionsTrace = voiceTracer("captions")

// Heartbeat cadence for the pacer trace: frequent enough to bracket a caption
// freeze in a trace export, sparse enough to stay negligible.
const PACER_TRACE_INTERVAL_MS = 2000

interface UseVoiceTranscriptPacerArgs {
  // The audible assistant response id — non-null exactly while assistant
  // audio is playing out of the WebRTC buffer (`output_audio_buffer.started`
  // sets it, buffer drain/clear releases it). Its lifetime IS the pacing
  // anchor; a value change while non-null is a gapless response handoff.
  windowKey: string | null
  // Concatenated text/revealed of the audible response's assistant items in
  // stream order (`assistantTranscriptPaced` fills them in the same order).
  text: string
  revealedLength: number
  syllablesPerSecond: number
  // Speech-gate state from the engine (true/false when the browser meters the
  // assistant's playback level, null when it doesn't): while false the
  // scheduler's clock freezes instead of estimating pauses. Passing null
  // keeps Phase 1 behavior.
  speaking: boolean | null
  // The engine's per-audio-window regime latch — the single owner the
  // scheduler follows for freeze/debit/resync decisions.
  windowGated: boolean
  onPace: (length: number) => void
}

/**
 * Advances the render-only reveal frontier for the engine-selected audio window.
 * Redux supplies the authoritative window identity and speech-gate regime; this
 * hook retains one scheduler across transcript deltas and gapless handoffs so
 * frame timing never creates a competing lifecycle authority.
 */
export function useVoiceTranscriptPacer({
  windowKey,
  text,
  revealedLength,
  syllablesPerSecond,
  speaking,
  windowGated,
  onPace,
}: UseVoiceTranscriptPacerArgs) {
  const tickArgsRef = useRef({
    windowKey,
    text,
    revealedLength,
    syllablesPerSecond,
    speaking,
    windowGated,
  })
  tickArgsRef.current = {
    windowKey,
    text,
    revealedLength,
    syllablesPerSecond,
    speaking,
    windowGated,
  }
  const onPaceRef = useRef(onPace)
  onPaceRef.current = onPace

  const active = windowKey !== null

  // requestAnimationFrame is an external render-timing subscription, so this
  // boundary stays in an effect. One scheduler runs for the whole audio
  // window — its deficit is what lets the caption absorb transcript-delta
  // stalls and background-tab gaps instead of drifting behind the audio — so
  // it must survive empty-queue frames and gapless handoffs; only the window
  // ending tears it down.
  useEffect(() => {
    if (!active) return

    const scheduler = new CaptionRevealScheduler()
    let frame: number
    let previousTimestamp: number | null = null
    let lastTraceAt: number | null = null
    let pacedChars = 0

    const tick = (timestamp: number) => {
      // Re-arm FIRST: a single throw below must not kill the loop for the
      // rest of the window (voice-trace-7369177 froze a caption for 30s).
      frame = requestAnimationFrame(tick)
      const dtSeconds =
        previousTimestamp === null ? 0 : (timestamp - previousTimestamp) / 1000
      previousTimestamp = timestamp

      const current = tickArgsRef.current
      if (current.windowKey === null) return
      const length = scheduler.tick({
        dtSeconds,
        windowKey: current.windowKey,
        text: current.text,
        revealedLength: current.revealedLength,
        syllablesPerSecond: current.syllablesPerSecond,
        speaking: current.speaking,
        windowGated: current.windowGated,
      })
      if (length > 0) {
        pacedChars += length
        onPaceRef.current(length)
      }
      // Heartbeat: proves the loop is alive and shows what it sees, so a
      // frozen caption is diagnosable from a trace export.
      if (
        lastTraceAt === null ||
        timestamp - lastTraceAt >= PACER_TRACE_INTERVAL_MS
      ) {
        lastTraceAt = timestamp
        captionsTrace.info("caption_pacer_tick", {
          textLength: current.text.length,
          revealedLength: current.revealedLength,
          syllablesPerSecond: current.syllablesPerSecond,
          speaking: current.speaking,
          engineWindowGated: current.windowGated,
          pacedChars,
          ...scheduler.snapshot(),
        })
      }
    }

    frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frame)
    }
  }, [active])
}
